/**
 * MapTilePrinter
 * 印刷範囲をキャプチャし高解像度キャンバスを生成するユーティリティ。
 *
 * map.project() はピクセル比変更後に座標が変わりズレが生じるため使用しない。
 * 代わりに map.getBounds() で実際の描画範囲を取得し、
 * 地理座標（Mercator Y）ベースでクロップ座標とマーカー位置を計算する。
 *
 * capture() は { canvas, renderedBounds } を返す。
 * renderedBounds: キャプチャ時の実際の描画地理範囲（マーカー位置計算に使う）
 */

const IDLE_TIMEOUT_MS  = 4000;
const PIXEL_RATIO_MULT = 3;

// Web Mercator の Y 座標（緯度 → ラジアン変換済みの値）
const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));

class MapTilePrinter {
	constructor(map) {
		this.map = map;
	}

	/**
	 * 指定バウンズをキャプチャし、高解像度キャンバスを返す。
	 *
	 * @param {object}   options
	 * @param {{ west, east, south, north }} options.bounds  印刷地理範囲
	 * @param {number}   options.outWidthPx
	 * @param {number}   options.outHeightPx
	 * @param {number}   [options.tileGrid=1]
	 * @param {function} [options.onProgress] (done, total) コールバック
	 * @returns {Promise<{ canvas, renderedBounds }>}
	 *   renderedBounds: キャプチャ時に実際に描画された地理範囲
	 *                  (マーカー位置計算に使う。tileGrid=1 では full bounds と等しい)
	 */
	async capture({ bounds, outWidthPx, outHeightPx, tileGrid = 1, onProgress = null }) {
		const map = this.map;
		const hasSetPixelRatio   = typeof map.setPixelRatio === 'function';
		const originalPixelRatio = hasSetPixelRatio
			? map.getPixelRatio()
			: (window.devicePixelRatio || 1);
		const capturePixelRatio  = originalPixelRatio * PIXEL_RATIO_MULT;

		const outCanvas = document.createElement('canvas');
		outCanvas.width  = outWidthPx;
		outCanvas.height = outHeightPx;
		const outCtx = outCanvas.getContext('2d');
		outCtx.imageSmoothingEnabled = true;
		outCtx.imageSmoothingQuality = 'high';

		const cols       = tileGrid;
		const rows       = tileGrid;
		const totalTiles = cols * rows;
		const tileLngW   = (bounds.east  - bounds.west)  / cols;
		const tileLatH   = (bounds.north - bounds.south) / rows;
		const destTileW  = outWidthPx  / cols;
		const destTileH  = outHeightPx / rows;

		// tileGrid=1 のとき、実際に描画された地理範囲を保存して返す
		let renderedBounds = null;

		try {
			if (hasSetPixelRatio) {
				map.setPixelRatio(capturePixelRatio);
				await this._waitForIdle(1000);
			}

			let doneCount = 0;
			for (let row = 0; row < rows; row++) {
				for (let col = 0; col < cols; col++) {
					const tileBounds = {
						west:  bounds.west  + col       * tileLngW,
						east:  bounds.west  + (col + 1) * tileLngW,
						south: bounds.south + row       * tileLatH,
						north: bounds.south + (row + 1) * tileLatH,
					};

					map.fitBounds(
						[[tileBounds.west, tileBounds.south], [tileBounds.east, tileBounds.north]],
						{ padding: 0, linear: true, animate: false }
					);
					await this._waitForIdle(IDLE_TIMEOUT_MS);

					// キャプチャ前に実際の描画範囲を取得
					const mb = map.getBounds();
					const actual = {
						west:  mb.getWest(),
						east:  mb.getEast(),
						north: mb.getNorth(),
						south: mb.getSouth(),
					};

					// render イベント内でキャプチャ（preserveDrawingBuffer:false 対応）
					const mapCanvas = await this._captureInRenderFrame();

					// map.project() を使わず地理座標ベースでクロップ領域を計算
					// Mercator では X（経度）は線形、Y は mercY() で非線形変換
					const aNY = mercY(actual.north);
					const aSY = mercY(actual.south);

					const x0f = (tileBounds.west  - actual.west)  / (actual.east  - actual.west);
					const x1f = (tileBounds.east  - actual.west)  / (actual.east  - actual.west);
					const y0f = (aNY - mercY(tileBounds.north)) / (aNY - aSY);
					const y1f = (aNY - mercY(tileBounds.south)) / (aNY - aSY);

					let cropX = Math.max(0, Math.round(x0f * mapCanvas.width));
					let cropY = Math.max(0, Math.round(y0f * mapCanvas.height));
					let cropW = Math.min(
						mapCanvas.width  - cropX,
						Math.round((x1f - x0f) * mapCanvas.width)
					);
					let cropH = Math.min(
						mapCanvas.height - cropY,
						Math.round((y1f - y0f) * mapCanvas.height)
					);

					if (cropW <= 0 || cropH <= 0) {
						cropX = 0; cropY = 0;
						cropW = mapCanvas.width; cropH = mapCanvas.height;
					}

					const destX = Math.round(col              * destTileW);
					const destY = Math.round((rows - 1 - row) * destTileH);

					outCtx.drawImage(
						mapCanvas,
						cropX, cropY, cropW, cropH,
						destX, destY, destTileW, destTileH
					);

					// 出力キャンバス全体は bounds（印刷範囲全体）に対応する。
					// マーカー位置計算用に、全タイル完了後に bounds を返す。
					renderedBounds = { ...bounds };

					doneCount++;
					if (onProgress) onProgress(doneCount, totalTiles);
				}
			}
		} finally {
			if (hasSetPixelRatio) map.setPixelRatio(originalPixelRatio);
		}

		return { canvas: outCanvas, renderedBounds };
	}

	/**
	 * render イベント内で WebGL キャンバスをコピーして返す。
	 * preserveDrawingBuffer: false でも描画フレーム中なのでバッファが有効。
	 */
	_captureInRenderFrame() {
		return new Promise(resolve => {
			const map = this.map;
			map.once('render', () => {
				const src = map.getCanvas();
				const dst = document.createElement('canvas');
				dst.width  = src.width;
				dst.height = src.height;
				dst.getContext('2d').drawImage(src, 0, 0);
				resolve(dst);
			});
			map.triggerRepaint();
		});
	}

	_waitForIdle(timeoutMs = IDLE_TIMEOUT_MS) {
		return new Promise(resolve => {
			let done = false;
			const finish = () => { if (!done) { done = true; resolve(); } };
			this.map.once('idle', finish);
			setTimeout(finish, timeoutMs);
		});
	}
}

export default MapTilePrinter;
